import {
  FieldConfig,
  FieldDataTypes,
  ModelStatus,
  Uuid,
} from '@hidat/huijs-common';
import { ZonedDateTime } from '@js-joda/core';
import * as dayjs from 'dayjs';

export interface NamedResource {
  id: Uuid | string | number | undefined;
  name: string | undefined;
  priority?: number;
}

/**
 * Resource that has a name associated with it.
 */
export interface NameableResource {
  name: string | undefined;
}

export interface ModelStatusResource {
  status: ModelStatus | undefined | null;
}

/**
 * Update Tracking Interface
 * Resource implements the TypeOrm created/updated at tracking fields
 */
export interface NestUpdateTrackingResource {
  createdAt: ZonedDateTime | dayjs.Dayjs | undefined;
  updatedAt: ZonedDateTime | dayjs.Dayjs | undefined;
  updatedCount: number | undefined;
}

/**
 * Update Tracking, as done by Rails
 * Doesn't have a updatedCount
 */
export interface RailsUpdateTrackingResource {
  createdAt: ZonedDateTime | dayjs.Dayjs | undefined;
  updatedAt: ZonedDateTime | dayjs.Dayjs | undefined;
}

/**
 * User Tracking Resource
 * Indicates the resource tracks creation/updated by users.
 */
export interface UserTrackingResource {
  createdById: Uuid | undefined;
  updatedById?: Uuid | undefined;
}

/**
 * Soft Delete Resources
 * A resource that gets marked as deleted, instead of actually being removed from the database.
 * Model status will be set to 'DELETED' and deleted by/time will be recorded.
 * Items can then be later purged.
 */
export interface SoftDeleteResource extends ModelStatusResource {
  deletedAt: ZonedDateTime | undefined | null;
  deletedById: Uuid | undefined | null;
}

/**
 * Indicates the resources has a 'related resource' fields.
 * This is a generic interface indicating that the object can be related to one of several resource types
 */
export interface HasRelatedResource {
  resourceType: string;
  resourceId: unknown;
  _relatedResource?: unknown;
}

/**
 * Has Tenant
 * Indicates the user tracks a tenant id as its owner
 */
export interface HasTenantResource {
  tenantId: Uuid | undefined;
}

const systemView = 'system';
/**
 * Resource System Fields
 * Set of FieldConfigs that most programs will use
 */
export const ResourceSystemFields = {
  intId: new FieldConfig(
    'id',
    FieldDataTypes.INTEGER,
    { canCreate: true, canUpdate: false },
    { canEdit: false },
  ).freeze(),
  guidId: new FieldConfig(
    'id',
    FieldDataTypes.UID,
    { canCreate: true, canUpdate: false },
    { canEdit: false },
  ).freeze(),
  status: new FieldConfig('status', FieldDataTypes.INTEGER, true).freeze(),
  createdAt: new FieldConfig('createdAt', FieldDataTypes.DATETIME, false)
    .setEditable(false)
    .addToView(systemView)
    .freeze(),
  updatedAt: new FieldConfig('updatedAt', FieldDataTypes.DATETIME, false)
    .setEditable(false)
    .addToView(systemView)
    .freeze(),
  deletedAt: new FieldConfig('deletedAt', FieldDataTypes.DATETIME, false)
    .setEditable(false)
    .addToView(systemView)
    .freeze(),
  createdBy: new FieldConfig('createdById', FieldDataTypes.UID, false)
    .setEditable(false)
    .addToView(systemView)
    .freeze(),
  updatedBy: new FieldConfig('updatedById', FieldDataTypes.UID, false)
    .setEditable(false)
    .addToView(systemView)
    .freeze(),
  deletedBy: new FieldConfig('deletedById', FieldDataTypes.UID, false)
    .setEditable(false)
    .addToView(systemView)
    .freeze(),
  updatedCount: new FieldConfig('updatedCount', FieldDataTypes.INTEGER, false)
    .setEditable(false)
    .addToView(systemView)
    .freeze(),
  tenantId: new FieldConfig('tenantId', FieldDataTypes.UID, false)
    .setEditable(false)
    .addToView(systemView)
    .freeze(),
  priority: new FieldConfig('priority', FieldDataTypes.INTEGER, true).freeze(),
  name: new FieldConfig('name', FieldDataTypes.STRING, true).freeze(),
};
